import React, { Component } from "react";
import axios from "axios";
import {
  Button,
  Modal,
  FormGroup,
  ControlLabel,
  FormControl,
  HelpBlock,
} from "react-bootstrap";
import {
  BootstrapTable,
  TableHeaderColumn,
  SearchField,
} from "react-bootstrap-table";

import { customLog, style, emailRegex } from "../../variables/Variables";
import { doGetLocklist, fire, auth } from "../../firebase/index";
import AddToListButton from "./AddToListButton";
import NotificationSystem from "react-notification-system";
import ReactTooltip from "react-tooltip";
import LocalizedStrings from "react-localization";

const localizationStrings = new LocalizedStrings({
  en: {
    memberEmailTitle: "Member Email",
    userAddedMessage1: "User has been added to your recipient list !",
    validError: "Please ensure your email and name are valid !",
    addedError: "This user is already added !",
    groupError:
      "This user is not part of this group ! Please contact the system administrator !",
    duplicateError: "Please make sure email & name are not duplicated !",
    emptyError: "Please enter something to edit !",
    addRecipientTitle: "Add Recipient",
    emailHeader: "Email address",
    nameHeader: "Name",
    submitButton: "Submit",
    closeButton: "Close",
    noDataTitle: "There is no data to display",
    allMembersTitle: "All Members",
    selectedRecipientsTitle: "Selected Recipients",
    searchPlaceholder: "Search",
    recipientTip:
      "A recipient is a selected member with user role that is chosen/added by an admin member to be managed.",
  },
  ja: {
    memberEmailTitle: "メンバーのメール",
    userAddedMessage1: "メンバーを登録しました。",
    validError: "Eメールと名前が正しいかご確認下さい。",
    addedError: "そのアカウントは既に登録済です。",
    groupError:
      "そのアカウントはこのグループに登録されていません。管理者へ確認して下さい。",
    duplicateError: "Eメールと名前が正しいかご確認下さい。",
    emptyError: "編集内容を入力して下さい。",
    addRecipientTitle: "メンバー追加",
    emailHeader: "Eメール",
    nameHeader: "名前",
    submitButton: "追加",
    closeButton: "閉じる",
    noDataTitle: "表示するデータがありません。",
    allMembersTitle: "全てのメンバー",
    selectedRecipientsTitle: "メンバーを選択",
    searchPlaceholder: "検索",
    recipientTip:
      "受信者は、管理者メンバーによって選択/追加されたユーザーの役割を持つメンバーです。",
  },
  es: {
    memberEmailTitle: "Correo Electrónico del Miembro",
    userAddedMessage1:
      "¡El usuario ha sido agregado a tu lista de destinatarios!",
    validError:
      "¡Por favor, asegúrate de que tu correo electrónico y nombre sean válidos!",
    addedError: "¡Este usuario ya ha sido agregado!",
    groupError:
      "¡Este usuario no forma parte de este grupo! ¡Por favor, contacta al administrador del sistema!",
    duplicateError:
      "¡Por favor, asegúrate de que el correo electrónico y el nombre no estén duplicados!",
    emptyError: "¡Por favor, introduce algo para editar!",
    addRecipientTitle: "Agregar Destinatario",
    emailHeader: "Dirección de Correo Electrónico",
    nameHeader: "Nombre",
    submitButton: "Enviar",
    closeButton: "Cerrar",
    noDataTitle: "No hay datos para mostrar",
    allMembersTitle: "Todos los Miembros",
    selectedRecipientsTitle: "Destinatarios Seleccionados",
    searchPlaceholder: "Buscar",
    recipientTip:
      "Un destinatario es un miembro seleccionado con el rol de usuario que ha sido elegido/agregado por un miembro administrador para ser gestionado.",
  },
});

function FieldGroup({ id, label, help, ...props }) {
  return (
    <FormGroup controlId={id}>
      <ControlLabel>{label}</ControlLabel>
      <FormControl {...props} />
      {help && <HelpBlock>{help}</HelpBlock>}
    </FormGroup>
  );
}

class Add extends Component {
  state = {
    membersArr: [],
    tempMembersArr: [],
    selectedMembersToAdd: [],
    show: false,
    locklistArray: [],
    isLoading: false,
    currentLanguage: "en",
  };

  notificationSystem = null;
  languageRef = null;

  async componentDidMount() {
    this.notificationSystem = this.refs.notificationSystem;

    try {
      await Promise.all([
        this.loadMembers(),
        this.loadLocklist(),
        this.subscribeLanguage(),
      ]);
    } catch (error) {
      customLog(error);
    }
  }

  componentWillUnmount() {
    if (this.languageRef && auth.currentUser) {
      fire
        .database()
        .ref("userSettings")
        .child(auth.currentUser.uid)
        .child("language")
        .off("value", this.languageRef);
    }
  }

  loadLocklist = async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_FIREBASE_DATABASE
        }/${doGetLocklist()}.json?auth=${
          process.env.REACT_APP_FIREBASE_DATABASE_SECRET
        }`,
      );

      this.setState({ locklistArray: response.data || [] });
    } catch (err) {
      customLog(err);
    }
  };

  subscribeLanguage = async () => {
    if (!auth.currentUser) return;

    const languageDbRef = fire
      .database()
      .ref("userSettings")
      .child(auth.currentUser.uid)
      .child("language");

    this.languageRef = (snapshot) => {
      this.setState({
        currentLanguage: snapshot.val() || "en",
      });
    };

    languageDbRef.on("value", this.languageRef);
  };

  loadMembers = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const db = fire.database();
    const groupAccountsRef = db.ref("groupAccounts");
    const userFriendListIndustrialRef = db.ref("userFriendListIndustrial");

    const [groupSnapshot, friendSnapshot] = await Promise.all([
      groupAccountsRef.once("value"),
      userFriendListIndustrialRef.child(currentUser.uid).once("value"),
    ]);

    const existingFriendEmails = new Set();

    if (friendSnapshot.exists()) {
      friendSnapshot.forEach((friend) => {
        const friendData = friend.val();
        if (friendData?.friendEmail) {
          existingFriendEmails.add(friendData.friendEmail);
        }
      });
    }

    const membersInsideGroup = [];

    groupSnapshot.forEach((superAdminDetails) => {
      const detailsVal = superAdminDetails.val();
      const members = detailsVal?.members || {};

      const belongsToThisGroup = Object.values(members).some(
        (member) => member?.memberEmail === currentUser.email,
      );

      if (!belongsToThisGroup) return;

      Object.values(members).forEach((member) => {
        if (
          member &&
          !member.isSuperAdmin &&
          member.memberEmail !== currentUser.email &&
          !existingFriendEmails.has(member.memberEmail)
        ) {
          membersInsideGroup.push(member);
        }
      });
    });

    this.setState({
      membersArr: membersInsideGroup,
      tempMembersArr: membersInsideGroup,
    });
  };

  handleShow = async () => {
    await this.loadMembers();
    this.setState({
      show: true,
      selectedMembersToAdd: [],
    });
  };
  handleClose = () => {
    this.setState({
      show: false,
      selectedMembersToAdd: [],
    });
  };

  showNotification = (msg, lvl, position = "tc", duration = 3) => {
    if (lvl === "success") {
      this.setState({ selectedMembersToAdd: [] });
    }

    if (!this.notificationSystem) return;

    this.notificationSystem.addNotification({
      title: <span data-notify="icon" className="pe-7s-bell"></span>,
      message: <div>{msg}</div>,
      level: lvl,
      position,
      autoDismiss: duration,
    });
  };

  updateSelectedMember = (data) => {
    const selectedMember = data?.[0];
    if (!selectedMember) return;

    this.setState((prevState) => ({
      membersArr: prevState.membersArr.filter(
        (member) => member.memberEmail !== selectedMember.memberEmail,
      ),
      selectedMembersToAdd: [...prevState.selectedMembersToAdd, selectedMember],
    }));
  };

  unselectRecipient = (email) => {
    return (
      <button
        className="unselect-btn"
        onClick={() => {
          this.setState((prevState) => {
            const selectedMember = prevState.selectedMembersToAdd.find(
              (member) => member.memberEmail === email,
            );

            if (!selectedMember) return null;

            return {
              membersArr: [...prevState.membersArr, selectedMember],
              selectedMembersToAdd: prevState.selectedMembersToAdd.filter(
                (member) => member.memberEmail !== email,
              ),
            };
          });
        }}
      >
        &times;
      </button>
    );
  };

  renderAddToRecipient = (cell, row) => {
    return (
      <AddToListButton data={row} dataCallBack={this.updateSelectedMember} />
    );
  };

  createCustomSearchField = () => {
    return (
      <SearchField
        className="formSearchBar"
        placeholder={localizationStrings.searchPlaceholder}
      />
    );
  };

  updateDatabase = async (uNameData, name, email) => {
    const { dataCallBack } = this.props;
    const currentUserID = auth.currentUser.uid;

    const userFriendListIndustrialDB = fire
      .database()
      .ref("userFriendListIndustrial");
    const registeredUsersOnPlatformDB = fire
      .database()
      .ref("registeredUsersOnPlatform");
    const historicalRecipientsDB = fire.database().ref("historicalRecipients");

    const snapshot = await registeredUsersOnPlatformDB
      .orderByValue()
      .once("value");

    if (email === snapshot.val()) return;

    const friendListKey = userFriendListIndustrialDB.child(currentUserID).push()
      .key;

    const industrialRecipientDetails = {
      allowAccess: true,
      friendEmail: email,
      friendName: name,
      id: friendListKey,
    };

    await userFriendListIndustrialDB
      .child(currentUserID)
      .child(friendListKey)
      .set(industrialRecipientDetails);

    const historicalKey = historicalRecipientsDB.child(currentUserID).push()
      .key;

    const recipientDetails = {
      dateTimeAdded: Date.now() / 1000,
      email,
      name,
      userName: uNameData,
      id: historicalKey,
    };

    await historicalRecipientsDB
      .child(currentUserID)
      .child(historicalKey)
      .set(recipientDetails);

    if (dataCallBack) {
      dataCallBack({
        memberName: uNameData,
        friendName: name,
        friendEmail: email,
        id: friendListKey,
      });
    }
  };

  validateAndAddRecipient = async (member) => {
    const email = member.memberEmail;
    const formattedName = email.split("@")[0].replace(/\./g, "_");
    const name = formattedName;
    const nameregexOnlySpace = /^[" "]+$/i;

    let errMsg = "";

    const userFriendListIndustrialDB = fire
      .database()
      .ref("userFriendListIndustrial");
    const groupAccountDB = fire.database().ref("groupAccounts");

    let existInGroupAccount = false;
    let existInUserFriendListIndustrial = false;
    let uNameData = "Unknown";

    const [groupSnapshot, friendSnapshot] = await Promise.all([
      groupAccountDB.once("value"),
      userFriendListIndustrialDB.child(auth.currentUser.uid).once("value"),
    ]);

    groupSnapshot.forEach((superAdminDetails) => {
      const detailsVal = superAdminDetails.val();
      const members = detailsVal?.members || {};

      const belongsToCurrentUserGroup = Object.values(members).some(
        (memberData) => memberData?.memberEmail === auth.currentUser.email,
      );

      if (!belongsToCurrentUserGroup) return;

      Object.values(members).forEach((memberData) => {
        if (memberData?.memberEmail === email) {
          existInGroupAccount = true;
          uNameData = memberData?.memberName || "Unknown";
        }
      });
    });

    if (friendSnapshot.exists()) {
      const snapshotVal = friendSnapshot.val();
      for (const eachFriend in snapshotVal) {
        if (snapshotVal[eachFriend]?.friendEmail === email) {
          existInUserFriendListIndustrial = true;
          break;
        }
      }
    }

    const emailValid = emailRegex.test(email);

    if (nameregexOnlySpace.test(name)) {
      errMsg = localizationStrings.emptyError;
      this.showNotification(errMsg, "error");
      return;
    }

    if (!existInGroupAccount) {
      errMsg = localizationStrings.groupError;
      this.showNotification(errMsg, "error");
      return;
    }

    if (existInUserFriendListIndustrial) {
      errMsg = localizationStrings.addedError;
      this.showNotification(errMsg, "error");
      return;
    }

    if (!emailValid) {
      errMsg = localizationStrings.validError;
      this.showNotification(errMsg, "error");
      return;
    }

    await this.updateDatabase(uNameData, name, email);
  };

  onSubmit = async () => {
    this.setState({ isLoading: true });

    try {
      const { selectedMembersToAdd } = this.state;

      for (const member of selectedMembersToAdd) {
        await this.validateAndAddRecipient(member);
      }

      this.handleClose();
      this.showNotification(localizationStrings.userAddedMessage1, "success");

      await this.loadMembers();
    } catch (error) {
      customLog(error);
      this.showNotification("Something went wrong.", "error");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    localizationStrings.setLanguage(this.state.currentLanguage || "en");

    const isInvalid = this.state.selectedMembersToAdd.length === 0;

    const options = {
      page: 1,
      sizePerPageList: [{ text: "5", value: 5 }],
      sizePerPage: 5,
      pagination: false,
      prePage: "<",
      nextPage: ">",
      firstPage: "<<",
      lastPage: ">>",
      searchField: this.createCustomSearchField,
      noDataText: localizationStrings.noDataTitle,
    };

    return (
      <div>
        <NotificationSystem ref="notificationSystem" style={style} />

        <div style={{ display: "flex" }}>
          <Button
            onClick={this.handleShow}
            className="addrecbutton"
            style={{ width: "unset" }}
          >
            <strong>{localizationStrings.addRecipientTitle}</strong>
          </Button>

          <div style={{ marginTop: "15px" }}>
            <a data-tip data-for="recipientTip">
              <span
                style={{
                  color: "#3C3E41",
                  fontSize: "21px",
                  marginLeft: "10px",
                }}
                className="glyphicon glyphicon-info-sign"
                aria-hidden="true"
              ></span>
            </a>
            <ReactTooltip id="recipientTip" effect="solid">
              <span>{localizationStrings.recipientTip}</span>
            </ReactTooltip>
          </div>
        </div>

        <Modal
          dialogClassName="modal-70w"
          show={this.state.show}
          onHide={this.handleClose}
        >
          <Modal.Header closeButton>
            <Modal.Title>{localizationStrings.addRecipientTitle}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <section style={{ margin: "0 15px" }}>
              <p style={{ fontSize: "18px", marginBottom: "-10px" }}>
                {localizationStrings.allMembersTitle}
              </p>

              <BootstrapTable
                id="all-members-table"
                pagination
                bordered={false}
                data={this.state.membersArr}
                options={options}
                search
                tableStyle={{
                  border: "3px solid lightgrey",
                  borderRadius: "8px",
                  marginTop: "20px",
                }}
              >
                <TableHeaderColumn
                  dataSort
                  hidden
                  dataField="memberUid"
                  isKey
                />
                <TableHeaderColumn
                  dataSort
                  dataField="memberEmail"
                  tdStyle={{ textAlign: "center" }}
                  thStyle={{ textAlign: "center" }}
                >
                  {localizationStrings.memberEmailTitle}
                </TableHeaderColumn>
                <TableHeaderColumn
                  dataField="memberEmail"
                  dataFormat={this.renderAddToRecipient}
                  tdStyle={{ textAlign: "center" }}
                  thStyle={{ textAlign: "center" }}
                />
              </BootstrapTable>

              <p style={{ fontSize: "18px" }}>
                {localizationStrings.selectedRecipientsTitle}
              </p>

              <BootstrapTable
                id="selected-members-table"
                pagination
                bordered={false}
                options={options}
                data={this.state.selectedMembersToAdd}
                tableStyle={{
                  border: "3px solid lightgrey",
                  borderRadius: "8px",
                  marginTop: "20px",
                }}
              >
                <TableHeaderColumn dataSort hidden dataField="memberUid" />
                <TableHeaderColumn
                  dataSort
                  dataField="memberEmail"
                  isKey
                  tdStyle={{ textAlign: "center" }}
                  thStyle={{ textAlign: "center" }}
                >
                  {localizationStrings.memberEmailTitle}
                </TableHeaderColumn>
                <TableHeaderColumn
                  dataField="memberEmail"
                  dataFormat={this.unselectRecipient}
                  tdStyle={{ textAlign: "center" }}
                  thStyle={{ textAlign: "center" }}
                />
              </BootstrapTable>
            </section>

            <div
              style={{ display: "flex", gap: "20px", margin: "0 15px 20px" }}
            >
              <Button
                className="btn btn-default"
                disabled={isInvalid || this.state.isLoading}
                type="submit"
                onClick={this.onSubmit}
              >
                {this.state.isLoading
                  ? "Processing..."
                  : localizationStrings.submitButton}
              </Button>

              <Button className="btn btn-default" onClick={this.handleClose}>
                {localizationStrings.closeButton}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

export default Add;
